import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Card,
  CardMedia,
} from "@mui/material";
import React, { useMemo } from "react";
import "./Highlights.css";
import { useNavigate } from "react-router-dom";
import { GalleryArray } from "../../../../helpers/gallery";

const Highlights = () => {
  const navigate = useNavigate();

  // Memoize shuffled and sliced array to prevent re-shuffling on every render
  const displayImages = useMemo(() => {
    if (!GalleryArray || GalleryArray.length === 0) return [];
    return [...GalleryArray].sort(() => Math.random() - 0.5).slice(0, 8);
  }, []);

  return (
    <Box
      className="highlights-section"
      sx={{
        width: "100%",
        backgroundColor: "primary.main",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          padding: { xs: "50px 20px", sm: "50px 60px", lg: "70px 120px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", sm: "space-between" },
            gap: { xs: "10px", sm: "0" },
            alignItems: "center",
            marginBottom: "0px",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="h2"
              className="categories-title"
              sx={{
                textAlign: { xs: "center", sm: "start" },
                fontSize: { xs: "32px", sm: "42px" },
                fontWeight: "700",
                color: "secondary.main",
                marginBottom: "8px",
                letterSpacing: "-0.5px",
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                width: "100%",
              }}
            >
              IRC Highlights
            </Typography>
            <Box
              sx={{
                height: "4px",
                width: { xs: "120px", md: "150px" },
                background: {
                  xs: "linear-gradient(90deg, transparent,#fff, transparent)",
                  sm: "linear-gradient(90deg, #fff, transparent)",
                },
                margin: { xs: "0 auto", sm: "0" },
                borderRadius: "2px",
                // boxShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
              }}
            />
          </Box>
          <Button
            variant="contained"
            color="secondary"
            href="/categories"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: "600",
              color: "primary.main",
              padding: { xs: "10px 28px", sm: "12px 36px" },
              borderRadius: "30px",
              textTransform: "none",
              letterSpacing: "0.5px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              minWidth: "230px",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 24px rgba(0, 0, 0, 0.3)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
              },
            }}
          >
            View All Gallery
          </Button>
        </Box>

        {displayImages.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              margin: "100px 0",
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 3 }}
            justifyContent="center"
            sx={{
              marginTop: { xs: "10px" },
            }}
          >
            {displayImages.map((item, i) => (
              <Grid
                item
                xs={6}
                sm={6}
                md={4}
                lg={3}
                key={`highlight-${i}`}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Card
                  className="highlight-card"
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                    borderRadius: "20px",
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
                      "& .highlight-overlay": {
                        opacity: 1,
                      },
                      "& .highlight-image": {
                        transform: "scale(1.1)",
                      },
                    },
                  }}
                  onClick={() => navigate("/gallery")}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "80%", // 1:1 aspect ratio
                      overflow: "hidden",
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={item}
                      alt={`IRC Highlight ${i + 1}`}
                      loading="lazy"
                      className="highlight-image"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition:
                          "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                    <Box
                      className="highlight-overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(to top, rgba(131, 13, 11, 0.8) 0%, transparent 60%)",
                        opacity: 0,
                        transition: "opacity 0.4s ease",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        padding: "20px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "secondary.main",
                          fontWeight: "600",
                          fontSize: "18px",
                          textShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        View More
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Highlights;

// RobotChallenge is one of the biggest competitions for self-made, autonomous and mobile robots worldw
